/**
 * Advanced mode (1.4 P18, redesigned in R04, revised in round 2): the
 * equipment's action list, the trigger + step editor modal, the relay map and
 * per-action test / manual runs. Pure UI over the action array the parent
 * (BayEquipmentConfig) saves through PUT /api/bays/:id/config (or the vending
 * twin).
 *
 * An action = a trigger (when it runs) + steps (what the relays do). Steps:
 * pulse, hold, wait (a time, the wash in progress, or any input), release,
 * coin drop. Every time value is in ms. A relay step lists every added board
 * and any relay can be ticked (boards fire at the same offset). Advanced never
 * copies the Simple layout; it starts empty.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Modal, ScrollView } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { useTheme } from '../../context/ThemeContext';
import { useOwnerScope } from '../../context/OwnerScopeContext';
import { useToast } from '../Toast';
import Input from '../Input';
import Picker from '../Picker';
import Button from '../Button';
import InlineConfirm from '../InlineConfirm';
import type { ColorTheme } from '../../utils/theme';
import { spacing, fontSize, fontWeight, borderRadius } from '../../utils/theme';
import { useModalBottomPadding } from '../../utils/useModalBottomPadding';

export interface StepBoard { controllerId: string; relays: number[] }
export type Step =
  | { type: 'relays'; controllerId: string; relays: number[]; onMs: number; also?: StepBoard[] }
  | { type: 'hold'; controllerId: string; relays: number[]; timeoutMs: number; also?: StepBoard[] }
  | { type: 'wait'; ms: number }
  | { type: 'release'; controllerId: string; relays: number[]; also?: StepBoard[] }
  | { type: 'coin_drop'; controllerId: string; relay: number; coinValueCents: number; pulseMs: number; gapMs: number; count?: number }
  | { type: 'wait_input'; controllerId: string; inputIndex: number; active: boolean; timeoutMs: number; onTimeout: 'fail' | 'continue'; role?: 'in_operation' | 'input' };
export type Trigger = 'tier_purchased' | 'upsell_purchased' | 'wash_purchased' | 'vending_purchased' | 'cycle_complete' | 'wash_in_progress' | 'out_of_service' | 'back_in_service' | 'session_ended' | 'manual' | 'schedule';
export interface Action {
  id?: string; kind: string; name: string; tierId?: string | null; upsellId?: string | null; priceCents?: number | null; displayOrder?: number; enabled?: boolean;
  delayAfterTierMs?: number; steps: Step[]; config?: Record<string, unknown>;
  trigger?: Trigger; triggerConfig?: { time?: string; days?: number[] } & Record<string, unknown>; lastFiredAt?: string | null;
}
export interface CtrlRow { controllerId: string; name: string | null; modelLabel: string; relayCount: number; inputCount?: number; online: boolean }
export interface InputRef { controllerId: string; inputIndex: number; function: string; label?: string | null }
interface Catalog { tiers: { id: string; name: string; priceInCents: number }[]; upsells: { id: string; name: string; priceCents: number; defaultDelayMs?: number }[] }

const MAX_STEPS = 32;
const MAX_MS = 120000;
const MAX_HOLD_MS = 4 * 60 * 60 * 1000;
const PALETTE = ['#245f73', '#733e24', '#d4930d', '#2e7d32', '#6a1b9a', '#00838f', '#c0392b', '#546e7a'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Round 2 wording: self-serve says "Wash Started", automatics keep tier / upsell, vending says "Credit purchased". */
export function triggerLabel(t: Trigger, bayType: string): string {
  switch (t) {
    case 'tier_purchased': return 'Wash tier purchased';
    case 'upsell_purchased': return 'Upsell purchased';
    case 'wash_purchased': return 'Wash Started';
    case 'vending_purchased': return 'Credit purchased';
    case 'cycle_complete': return 'Wash Complete';
    case 'wash_in_progress': return 'Wash In Progress';
    case 'out_of_service': return 'Wash out of service';
    case 'back_in_service': return 'Wash back in service';
    case 'session_ended': return bayType === 'vending' ? 'Credit delivered' : 'Session ended / stop pressed';
    case 'manual': return 'Manual (owner or staff button)';
    case 'schedule': return 'Schedule (time of day)';
  }
}
export function triggersForBay(bayType: string, hasInputs = true): Trigger[] {
  // Input triggers (Wash In Progress / Wash Complete from the Wash in progress input, out of / back in service from the In service input) are automatics only.
  const base: Trigger[] = bayType === 'automatic'
    ? ['tier_purchased', 'upsell_purchased', 'wash_in_progress', 'cycle_complete', 'out_of_service', 'back_in_service', 'manual', 'schedule']   // session_ended is for count-up equipment only
    : bayType === 'vending'
      ? ['vending_purchased', 'manual', 'schedule']
      : ['wash_purchased', 'session_ended', 'manual', 'schedule'];
  const inputTriggers: Trigger[] = ['wash_in_progress', 'cycle_complete', 'out_of_service', 'back_in_service'];
  return hasInputs ? base : base.filter(t => !inputTriggers.includes(t));
}
export function kindForTrigger(t: Trigger): string {
  return t === 'tier_purchased' ? 'tier' : t === 'upsell_purchased' ? 'upsell' : t === 'wash_purchased' ? 'wash' : t === 'vending_purchased' ? 'vending_credit' : 'custom';
}
/** Every (board, relays) pair a step drives; empty for waits. */
export function stepRelays(s: Step): StepBoard[] {
  if (s.type === 'relays' || s.type === 'hold' || s.type === 'release') return [{ controllerId: s.controllerId, relays: s.relays }, ...(s.also ?? []).filter(b => b.relays.length)];
  if (s.type === 'coin_drop') return [{ controllerId: s.controllerId, relays: [s.relay] }];
  return [];
}
const num = (v: string) => parseInt(v.replace(/[^0-9]/g, ''), 10) || 0;

export function actionTotalMs(a: Action): number {
  let t = 0; let end = 0;
  for (const s of a.steps) {
    if (s.type === 'wait') t += s.ms;
    else if (s.type === 'relays') { if (s.onMs > 0) t += s.onMs; end = Math.max(end, t); }
    else if (s.type === 'hold') end = Math.max(end, t + (s.timeoutMs || 0));
    else if (s.type === 'coin_drop') end = Math.max(end, t + 4 * (s.pulseMs + s.gapMs));
  }
  return Math.max(t, end);
}

/** Relay map: grid R1..Rn per controller with action names; red when two actions share a relay. */
export function RelayMapCard({ ctrls, actions, foreignUse }: { ctrls: CtrlRow[]; actions: Action[]; foreignUse: Record<string, number[]> }) {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  return (
    <View>
      {ctrls.map(c => {
        const byRelay: Record<number, string[]> = {};
        actions.forEach(a => a.steps.forEach(s => stepRelays(s).forEach(b => { if (b.controllerId === c.controllerId) b.relays.forEach(r => { (byRelay[r] ??= []).push(a.name); }); })));
        return (
          <View key={c.controllerId} style={styles.mapCard}>
            <Text style={styles.mapTitle}>{c.name || c.controllerId} · {c.modelLabel}</Text>
            <View style={styles.grid}>
              {Array.from({ length: c.relayCount }, (_, i) => i + 1).map(r => {
                const names = [...new Set(byRelay[r] ?? [])];
                const foreign = (foreignUse[c.controllerId] ?? []).includes(r);
                const conflict = names.length > 1;
                return (
                  <View key={r} style={[styles.cell, names.length === 0 && !foreign && styles.cellFree, conflict && styles.cellConflict, foreign && styles.cellForeign]}>
                    <Text style={styles.cellLabel}>R{r}</Text>
                    <Text style={[styles.cellText, conflict && { color: colors.danger }]} numberOfLines={2}>{foreign ? 'Other equipment' : names.length ? names.join(' + ') : 'free'}</Text>
                  </View>
                );
              })}
            </View>
          </View>
        );
      })}
    </View>
  );
}

/** Timeline preview: one row per (controller, relay) touched, bars at their start/length (sequential model). */
function Timeline({ steps, ctrls }: { steps: Step[]; ctrls: CtrlRow[] }) {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const rows: { key: string; label: string; color: string; bars: { t: number; len: number; open?: boolean }[] }[] = [];
  let t = 0; let total = 0;
  const ctrlIdx = (id: string) => Math.max(0, ctrls.findIndex(c => c.controllerId === id));
  const rowFor = (controllerId: string, r: number) => {
    const key = `${controllerId}:${r}`;
    let row = rows.find(x => x.key === key);
    if (!row) { row = { key, label: `${ctrls.find(c => c.controllerId === controllerId)?.name || 'C' + (ctrlIdx(controllerId) + 1)} R${r}`, color: PALETTE[ctrlIdx(controllerId) % PALETTE.length], bars: [] }; rows.push(row); }
    return row;
  };
  for (const s of steps) {
    if (s.type === 'wait') { t += s.ms; total = Math.max(total, t); continue; }
    if (s.type === 'wait_input') continue;
    if (s.type === 'relays') { for (const b of stepRelays(s)) for (const r of b.relays) rowFor(b.controllerId, r).bars.push({ t, len: s.onMs || 500, open: !s.onMs }); if (s.onMs > 0) t += s.onMs; total = Math.max(total, t, t + (s.onMs ? 0 : 500)); continue; }
    if (s.type === 'hold') { for (const b of stepRelays(s)) for (const r of b.relays) rowFor(b.controllerId, r).bars.push({ t, len: s.timeoutMs || 1000, open: !s.timeoutMs }); total = Math.max(total, t + (s.timeoutMs || 1000)); continue; }
    if (s.type === 'coin_drop') { const len = 4 * (s.pulseMs + s.gapMs); rowFor(s.controllerId, s.relay).bars.push({ t, len }); total = Math.max(total, t + len); continue; }
    if (s.type === 'release') { for (const b of stepRelays(s)) for (const r of b.relays) { const row = rowFor(b.controllerId, r); const last = row.bars[row.bars.length - 1]; if (last?.open) { last.len = Math.max(50, t - last.t); last.open = false; } } }
  }
  if (!rows.length) return null;
  const span = Math.max(total, 1);
  return (
    <View style={styles.timeline}>
      {rows.map(r => (
        <View key={r.key} style={styles.tlRow}>
          <Text style={styles.tlLabel} numberOfLines={1}>{r.label}</Text>
          <View style={styles.tlTrack}>
            {r.bars.map((b, i) => <View key={i} style={[styles.tlBar, { left: `${(b.t / span) * 100}%`, width: `${Math.max(1, (b.len / span) * 100)}%`, backgroundColor: r.color, opacity: b.open ? 0.5 : 1 }]} />)}
          </View>
        </View>
      ))}
      <Text style={styles.tlTotal}>About {total} ms{steps.some(s => s.type === 'wait_input') ? ' plus input waits' : ''}</Text>
    </View>
  );
}

type StepKind = 'relays' | 'hold' | 'wait' | 'release' | 'coin_drop';
const STEP_LABEL: Record<StepKind, string> = { relays: 'Pulse', hold: 'Hold', wait: 'Wait', release: 'Release', coin_drop: 'Coin drop' };
type WaitMode = 'time' | 'in_operation' | 'input';
const waitModeOf = (s: Step): WaitMode => s.type === 'wait' ? 'time' : s.type === 'wait_input' && s.role === 'in_operation' ? 'in_operation' : 'input';
const stepKindOf = (s: Step): StepKind => (s.type === 'wait_input' ? 'wait' : s.type);

interface EditorProps { visible: boolean; action: Action | null; bayType: string; ctrls: CtrlRow[]; inputs: InputRef[]; catalog: Catalog; onClose: () => void; onSave: (a: Action) => void }

function StepEditorModal({ visible, action, bayType, ctrls, inputs, catalog, onClose, onSave }: EditorProps) {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const pad = useModalBottomPadding();
  const inputCtrls = ctrls.filter(c => (c.inputCount ?? 0) > 0);
  const triggers = triggersForBay(bayType, inputCtrls.length > 0);
  const inOpInputs = inputs.filter(i => i.function === 'in_operation');
  const inOpInput = inOpInputs[0];
  const boardName = (id: string) => { const c = ctrls.find(x => x.controllerId === id); return c ? `${c.name || c.controllerId} (${c.modelLabel})` : id; };
  const [draft, setDraft] = useState<Action>({ kind: 'custom', name: '', steps: [], trigger: 'manual' });
  const [delayStr, setDelayStr] = useState('');
  useEffect(() => {
    if (!visible) return;
    const a: Action = action ?? { kind: 'custom', name: '', trigger: triggers[0], steps: [{ type: 'relays', controllerId: ctrls[0]?.controllerId ?? '', relays: [], onMs: 300 }] };
    const copy: Action = JSON.parse(JSON.stringify(a));
    if (!copy.trigger) copy.trigger = copy.kind === 'tier' ? 'tier_purchased' : copy.kind === 'upsell' ? 'upsell_purchased' : ['coin_drop', 'timer', 'count_up', 'wash'].includes(copy.kind) ? 'wash_purchased' : copy.kind === 'vending_credit' ? 'vending_purchased' : 'manual';
    setDraft(copy);
    setDelayStr(copy.delayAfterTierMs ? String(copy.delayAfterTierMs) : '');
  }, [visible, action, ctrls]);

  const setStep = (i: number, s: Step) => setDraft({ ...draft, steps: draft.steps.map((x, j) => (j === i ? s : x)) });
  const move = (i: number, d: -1 | 1) => { const st = [...draft.steps]; const j = i + d; if (j < 0 || j >= st.length) return; [st[i], st[j]] = [st[j], st[i]]; setDraft({ ...draft, steps: st }); };
  const firstCtrl = ctrls[0]?.controllerId ?? '';
  const newStep = (kind: StepKind): Step => {
    switch (kind) {
      case 'relays': return { type: 'relays', controllerId: firstCtrl, relays: [], onMs: 300 };
      case 'hold': return { type: 'hold', controllerId: firstCtrl, relays: [], timeoutMs: 0 };
      case 'wait': return { type: 'wait', ms: 1000 };
      case 'release': return { type: 'release', controllerId: firstCtrl, relays: [] };
      case 'coin_drop': return { type: 'coin_drop', controllerId: firstCtrl, relay: 1, coinValueCents: 25, pulseMs: 70, gapMs: 70 };
    }
  };
  const changeKind = (i: number, kind: StepKind) => {
    const cur = draft.steps[i]; if (stepKindOf(cur) === kind) return;
    const n = newStep(kind);
    if ((n.type === 'relays' || n.type === 'hold' || n.type === 'release') && (cur.type === 'relays' || cur.type === 'hold' || cur.type === 'release')) { n.controllerId = cur.controllerId; n.relays = cur.relays; n.also = cur.also; }
    setStep(i, n);
  };
  const setWaitMode = (i: number, mode: WaitMode) => {
    const cur = draft.steps[i]; if (waitModeOf(cur) === mode && (cur.type === 'wait' || cur.type === 'wait_input')) return;
    if (mode === 'time') { setStep(i, { type: 'wait', ms: 1000 }); return; }
    const base = cur.type === 'wait_input' ? cur : { timeoutMs: 30000, onTimeout: 'fail' as const };
    if (mode === 'in_operation') setStep(i, { type: 'wait_input', controllerId: inOpInput?.controllerId ?? '', inputIndex: inOpInput?.inputIndex ?? 0, active: false, timeoutMs: base.timeoutMs, onTimeout: base.onTimeout, role: 'in_operation' });
    // (board/index can be changed in the step when several boards carry a Wash in progress input)
    else setStep(i, { type: 'wait_input', controllerId: inputCtrls[0]?.controllerId ?? firstCtrl, inputIndex: 1, active: true, timeoutMs: base.timeoutMs, onTimeout: base.onTimeout, role: 'input' });
  };
  /** Relay chips for every board: the first board with relays is the primary, the rest go to `also`. */
  const setBoardRelays = (i: number, s: Extract<Step, { type: 'relays' | 'hold' | 'release' }>, controllerId: string, relays: number[]) => {
    const map = new Map<string, number[]>();
    for (const b of stepRelays(s)) map.set(b.controllerId, b.relays);
    if (relays.length) map.set(controllerId, relays); else map.delete(controllerId);
    const entries = [...map.entries()];
    const primary = entries.find(([id]) => id === s.controllerId) ?? entries[0];
    const next: Step = { ...s, controllerId: primary ? primary[0] : (controllerId || s.controllerId), relays: primary ? primary[1] : [], also: entries.filter(e => e !== primary).map(([id, r]) => ({ controllerId: id, relays: r })) };
    if (!next.also?.length) delete next.also;
    setStep(i, next);
  };

  const issues: string[] = [];
  if (!draft.name.trim()) issues.push('Name the action');
  if (draft.trigger === 'tier_purchased' && !draft.tierId) issues.push('Pick the wash tier');
  if (draft.trigger === 'upsell_purchased' && !draft.upsellId) issues.push('Pick the upsell');
  if (draft.trigger === 'schedule') {
    const tc = draft.triggerConfig ?? {};
    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(String(tc.time ?? ''))) issues.push('Schedule needs a time (HH:MM, 24 h)');
    if (!tc.days?.length) issues.push('Schedule needs at least one day');
  }
  if ((draft.trigger === 'cycle_complete' || draft.trigger === 'wash_in_progress') && !inputs.some(i => i.function === 'in_operation')) issues.push('This trigger needs an input set to Wash in progress on one of the boards');
  if ((draft.trigger === 'out_of_service' || draft.trigger === 'back_in_service') && !inputs.some(i => i.function === 'out_of_service')) issues.push('This trigger needs an input set to In service on one of the boards');
  if (!draft.steps.length) issues.push('Add at least one step');
  if (draft.steps.length > MAX_STEPS) issues.push(`At most ${MAX_STEPS} steps`);
  if (!draft.steps.some(s => stepRelays(s).length)) issues.push('Add a step that drives a relay');
  draft.steps.forEach((s, i) => {
    const n = i + 1;
    if (s.type === 'wait') { if (!(s.ms > 0) || s.ms > MAX_MS) issues.push(`Step ${n}: wait must be 1–${MAX_MS} ms`); return; }
    if (s.type === 'wait_input') {
      const c = ctrls.find(x => x.controllerId === s.controllerId);
      if (s.role === 'in_operation') { if (!inOpInput) issues.push(`Step ${n}: "The wash in progress" needs an input set to In operation (wash running) in the Inputs section below`); }
      else if (!c || !(c.inputCount ?? 0) || s.inputIndex < 1 || s.inputIndex > (c.inputCount ?? 0)) issues.push(`Step ${n}: that board has no input ${s.inputIndex}`);
      if (s.timeoutMs < 0 || s.timeoutMs > MAX_HOLD_MS) issues.push(`Step ${n}: timeout must be 0–${MAX_HOLD_MS} ms`);
      return;
    }
    const targets = stepRelays(s);
    if (!targets.length || targets.every(b => !b.relays.length)) issues.push(`Step ${n}: pick at least one relay`);
    for (const b of targets) {
      const c = ctrls.find(x => x.controllerId === b.controllerId);
      if (!c) { issues.push(`Step ${n}: pick a board`); continue; }
      if (b.relays.some(r => r < 1 || r > c.relayCount)) issues.push(`Step ${n}: relay out of range for ${c.modelLabel}`);
    }
    if (s.type === 'relays' && (s.onMs < 0 || s.onMs > MAX_MS)) issues.push(`Step ${n}: pulse must be 0–${MAX_MS} ms`);
    if (s.type === 'hold' && (s.timeoutMs < 0 || s.timeoutMs > MAX_HOLD_MS)) issues.push(`Step ${n}: hold timeout must be 0–${MAX_HOLD_MS} ms`);
    if (s.type === 'coin_drop' && (s.coinValueCents < 1 || s.pulseMs < 10 || s.gapMs < 10)) issues.push(`Step ${n}: coin value, pulse and gap must be set`);
  });

  const save = () => {
    if (issues.length) return;
    const delay = delayStr.trim() ? num(delayStr) : 0;
    const trigger = draft.trigger ?? 'manual';
    onSave({ ...draft, kind: kindForTrigger(trigger), trigger, name: draft.name.trim(), enabled: draft.enabled !== false, priceCents: null, delayAfterTierMs: trigger === 'upsell_purchased' ? delay : 0 });
  };
  const inputOptions = (controllerId: string) => {
    const c = ctrls.find(x => x.controllerId === controllerId);
    return Array.from({ length: c?.inputCount ?? 0 }, (_, k) => k + 1).map(n => { const ref = inputs.find(i => i.controllerId === controllerId && i.inputIndex === n); return { value: String(n), label: `Input ${n}${ref && ref.function !== 'none' ? ` · ${ref.label || ref.function.replace('_', ' ')}` : ''}` }; });
  };
  const BoardRelayChips = ({ s, i }: { s: Extract<Step, { type: 'relays' | 'hold' | 'release' }>; i: number }) => (
    <View>
      {ctrls.map(c => {
        const sel = stepRelays(s).find(b => b.controllerId === c.controllerId)?.relays ?? [];
        return (
          <View key={c.controllerId} style={styles.boardRow}>
            {ctrls.length > 1 && <Text style={styles.boardName}>{c.name || c.controllerId} · {c.modelLabel}</Text>}
            <View style={styles.chips}>
              {Array.from({ length: c.relayCount }, (_, k) => k + 1).map(r => {
                const on = sel.includes(r);
                return (
                  <TouchableOpacity key={r} style={[styles.chip, on && styles.chipOn]} onPress={() => setBoardRelays(i, s, c.controllerId, on ? sel.filter(x => x !== r) : [...sel, r].sort((a, b) => a - b))} accessibilityRole="checkbox" accessibilityState={{ checked: on }}>
                    <Text style={[styles.chipText, on && styles.chipTextOn]}>R{r}</Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>
        );
      })}
      {ctrls.length > 1 && <Text style={styles.help}>Separate controllers may have slightly asynchronous activation times.</Text>}
    </View>
  );
  const holdHelp = bayType === 'automatic' ? 'Timeout 0 = held until the wash ends (Input: Wash Complete), the bay goes out of service, or the bay maximum.' : bayType === 'vending' ? 'Timeout 0 = held until the program is cancelled or the maximum.' : 'Timeout 0 = held until the customer stops, the paid time runs out, or the bay maximum.';
  const unit = bayType === 'vacuum' ? 'vacuum' : bayType === 'vending' ? 'machine' : 'bay';

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.backdrop}>
        <View style={[styles.sheet, { paddingBottom: pad + spacing.md }]}>
          <ScrollView keyboardShouldPersistTaps="handled">
            <Text style={styles.sheetTitle}>{action ? 'Edit action' : 'New action'}</Text>
            <Picker label="Runs when" value={draft.trigger ?? 'manual'} onValueChange={v => setDraft({ ...draft, trigger: v as Trigger, tierId: null, upsellId: null, triggerConfig: v === 'schedule' ? { time: '03:00', days: [1, 2, 3, 4, 5] } : {} })}
              options={triggers.map(t => ({ value: t, label: triggerLabel(t, bayType) }))} />
            {draft.trigger === 'tier_purchased' && (
              <Picker label="Wash tier" value={draft.tierId ?? ''} onValueChange={v => { const t = catalog.tiers.find(x => x.id === v); setDraft({ ...draft, tierId: v || null, name: t?.name ?? draft.name }); }}
                options={[{ value: '', label: 'Pick a tier…' }, ...catalog.tiers.map(t => ({ value: t.id, label: `${t.name} ($${(t.priceInCents / 100).toFixed(2)})` }))]} />
            )}
            {draft.trigger === 'upsell_purchased' && (
              <Picker label="Upsell" value={draft.upsellId ?? ''} onValueChange={v => { const u = catalog.upsells.find(x => x.id === v); setDraft({ ...draft, upsellId: v || null, name: u?.name ?? draft.name }); if (u?.defaultDelayMs && !delayStr) setDelayStr(String(u.defaultDelayMs)); }}
                options={[{ value: '', label: 'Pick an upsell…' }, ...catalog.upsells.map(u => ({ value: u.id, label: `${u.name} ($${(u.priceCents / 100).toFixed(2)})` }))]} />
            )}
            {draft.trigger === 'wash_purchased' && <Text style={styles.help}>Runs when a customer pays for this {unit}. A coin-drop step sends as many pulses as the amount paid buys; a hold step with timeout 0 stays on for the paid time.</Text>}
            {draft.trigger === 'vending_purchased' && <Text style={styles.help}>Runs when a customer buys credit. A coin-drop step sends as many pulses as the credit amount buys.</Text>}
            {draft.trigger === 'session_ended' && <Text style={styles.help}>Runs when the customer stops the wash, the paid time runs out, or the maximum is reached.</Text>}
            {draft.trigger === 'manual' && <Text style={styles.help}>Runs from the Run button here, the owner dashboard, the location page, and staff with the Equipment buttons permission. Nothing is sold.</Text>}
            {draft.trigger === 'schedule' && (
              <>
                <Input label="Time of day (HH:MM, 24 h, location time)" value={String(draft.triggerConfig?.time ?? '')} onChangeText={v => setDraft({ ...draft, triggerConfig: { ...(draft.triggerConfig ?? {}), time: v.replace(/[^0-9:]/g, '').slice(0, 5) } })} keyboardType="numbers-and-punctuation" />
                <View style={styles.chips}>
                  {DAYS.map((d, i) => { const on = (draft.triggerConfig?.days ?? []).includes(i); return (
                    <TouchableOpacity key={d} style={[styles.chip, on && styles.chipOn]} onPress={() => { const days = new Set(draft.triggerConfig?.days ?? []); if (on) days.delete(i); else days.add(i); setDraft({ ...draft, triggerConfig: { ...(draft.triggerConfig ?? {}), days: [...days].sort() } }); }}>
                      <Text style={[styles.chipText, on && styles.chipTextOn]}>{d}</Text>
                    </TouchableOpacity>
                  ); })}
                </View>
              </>
            )}
            <Input label="Name" value={draft.name} onChangeText={v => setDraft({ ...draft, name: v })} maxLength={80} />
            {draft.trigger === 'upsell_purchased' && (
              <Input label="Delay after wash tier purchase trigger (ms)" value={delayStr} onChangeText={v => setDelayStr(v.replace(/[^0-9]/g, ''))} keyboardType="number-pad" />
            )}

            <Text style={styles.sub}>Steps (run in order)</Text>
            {draft.steps.map((s, i) => (
              <View key={i} style={styles.stepCard}>
                <View style={styles.stepHead}>
                  <Text style={styles.stepNum}>{i + 1}</Text>
                  <View style={{ flex: 1 }}>
                    <Picker value={stepKindOf(s)} onValueChange={v => changeKind(i, v as StepKind)} options={(Object.keys(STEP_LABEL) as StepKind[]).map(t => ({ value: t, label: STEP_LABEL[t] }))} />
                  </View>
                  <TouchableOpacity onPress={() => move(i, -1)} hitSlop={6}><Ionicons name="chevron-up" size={18} color={i === 0 ? colors.borderLight : colors.textSecondary} /></TouchableOpacity>
                  <TouchableOpacity onPress={() => move(i, 1)} hitSlop={6}><Ionicons name="chevron-down" size={18} color={i === draft.steps.length - 1 ? colors.borderLight : colors.textSecondary} /></TouchableOpacity>
                  <TouchableOpacity onPress={() => setDraft({ ...draft, steps: draft.steps.filter((_, j) => j !== i) })} hitSlop={6}><Ionicons name="trash-outline" size={18} color={colors.danger} /></TouchableOpacity>
                </View>
                {(s.type === 'wait' || s.type === 'wait_input') && (
                  <>
                    <View style={styles.segment}>
                      {([['time', 'A time'], ['in_operation', 'The wash in progress'], ['input', 'Any input']] as const).filter(([m]) => m === 'time' || inputCtrls.length > 0).map(([m, label]) => (
                        <TouchableOpacity key={m} style={[styles.segBtn, waitModeOf(s) === m && styles.segBtnActive]} onPress={() => setWaitMode(i, m)}><Text style={[styles.segText, waitModeOf(s) === m && styles.segTextActive]}>{label}</Text></TouchableOpacity>
                      ))}
                    </View>
                    {s.type === 'wait' && <Input label="Wait (ms)" value={String(s.ms)} onChangeText={v => setStep(i, { type: 'wait', ms: num(v) })} keyboardType="number-pad" />}
                    {s.type === 'wait_input' && (
                      <>
                        {s.role === 'in_operation'
                          ? (inOpInputs.length === 0
                            ? <Text style={styles.help}>Set an input to Wash in progress on one of the boards (each board's inputs sit under it in Controller Setup) first.</Text>
                            : inOpInputs.length === 1
                              ? <Text style={styles.help}>Watches Wash in progress on {boardName(inOpInputs[0].controllerId)}, input {inOpInputs[0].inputIndex}.</Text>
                              : <Picker label="Board (Wash in progress input)" options={inOpInputs.map(r => ({ value: r.controllerId, label: `${boardName(r.controllerId)} · input ${r.inputIndex}` }))} value={s.controllerId} onValueChange={v => { const r = inOpInputs.find(x => x.controllerId === v); setStep(i, { ...s, controllerId: v, inputIndex: r?.inputIndex ?? 0 }); }} />)
                          : (
                            <>
                              {inputCtrls.length > 1
                                ? <Picker label="Board" options={inputCtrls.map(c => ({ value: c.controllerId, label: `${c.name || c.controllerId} (${c.modelLabel})` }))} value={s.controllerId} onValueChange={v => setStep(i, { ...s, controllerId: v, inputIndex: 1 })} />
                                : <Text style={styles.help}>Board: {boardName(s.controllerId)}</Text>}
                              <Picker label="Input" options={inputOptions(s.controllerId)} value={String(s.inputIndex)} onValueChange={v => setStep(i, { ...s, inputIndex: parseInt(v) || 1 })} />
                            </>
                          )}
                        <View style={styles.segment}>
                          {([[false, s.role === 'in_operation' ? 'Until the wash ends (goes inactive)' : 'Until it goes inactive'], [true, s.role === 'in_operation' ? 'Until a wash starts (goes active)' : 'Until it goes active']] as const).map(([val, label]) => (
                            <TouchableOpacity key={String(val)} style={[styles.segBtn, s.active === val && styles.segBtnActive]} onPress={() => setStep(i, { ...s, active: val })}><Text style={[styles.segText, s.active === val && styles.segTextActive]}>{label}</Text></TouchableOpacity>
                          ))}
                        </View>
                        <Input label="Timeout (ms, at least 1000)" value={String(s.timeoutMs)} onChangeText={v => setStep(i, { ...s, timeoutMs: num(v) })} keyboardType="number-pad" />
                        <View style={styles.segment}>
                          {([['fail', 'On timeout: fail (stop, refund)'], ['continue', 'On timeout: continue']] as const).map(([val, label]) => (
                            <TouchableOpacity key={val} style={[styles.segBtn, s.onTimeout === val && styles.segBtnActive]} onPress={() => setStep(i, { ...s, onTimeout: val })}><Text style={[styles.segText, s.onTimeout === val && styles.segTextActive]}>{label}</Text></TouchableOpacity>
                          ))}
                        </View>
                      </>
                    )}
                  </>
                )}
                {(s.type === 'relays' || s.type === 'hold' || s.type === 'release') && (
                  <>
                    <BoardRelayChips s={s} i={i} />
                    {s.type === 'relays' && <Input label="Pulse for (ms, 0 = hold until the program ends)" value={String(s.onMs)} onChangeText={v => setStep(i, { ...s, onMs: num(v) })} keyboardType="number-pad" />}
                    {s.type === 'hold' && (
                      <>
                        <Input label="Hold timeout (ms, 0 = until the session ends)" value={String(s.timeoutMs)} onChangeText={v => setStep(i, { ...s, timeoutMs: num(v) })} keyboardType="number-pad" />
                        <Text style={styles.help}>{holdHelp}</Text>
                      </>
                    )}
                    {s.type === 'release' && <Text style={styles.help}>Turns the chosen relays off (ends an earlier hold).</Text>}
                  </>
                )}
                {s.type === 'coin_drop' && (
                  <>
                    {ctrls.length > 1 && <Picker options={ctrls.map(c => ({ value: c.controllerId, label: `${c.name || c.controllerId} (${c.modelLabel})` }))} value={s.controllerId} onValueChange={v => setStep(i, { ...s, controllerId: v, relay: 1 })} />}
                    <View style={styles.chips}>
                      {Array.from({ length: ctrls.find(c => c.controllerId === s.controllerId)?.relayCount ?? 0 }, (_, k) => k + 1).map(r => (
                        <TouchableOpacity key={r} style={[styles.chip, s.relay === r && styles.chipOn]} onPress={() => setStep(i, { ...s, relay: r })} accessibilityRole="radio" accessibilityState={{ selected: s.relay === r }}>
                          <Text style={[styles.chipText, s.relay === r && styles.chipTextOn]}>R{r}</Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                    <View style={styles.row2}>
                      <View style={{ flex: 1 }}><Input label="Coin value (¢)" value={String(s.coinValueCents)} onChangeText={v => setStep(i, { ...s, coinValueCents: num(v) })} keyboardType="number-pad" /></View>
                      <View style={{ flex: 1 }}><Input label="Pulse (ms)" value={String(s.pulseMs)} onChangeText={v => setStep(i, { ...s, pulseMs: num(v) })} keyboardType="number-pad" /></View>
                      <View style={{ flex: 1 }}><Input label="Gap (ms)" value={String(s.gapMs)} onChangeText={v => setStep(i, { ...s, gapMs: num(v) })} keyboardType="number-pad" /></View>
                    </View>
                    <Text style={styles.help}>The number of pulses always comes from the amount paid divided by the coin value.</Text>
                  </>
                )}
              </View>
            ))}
            <View style={[styles.row2, { flexWrap: 'wrap' }]}>
              {(Object.keys(STEP_LABEL) as StepKind[]).map(t => (
                <Button key={t} title={`+ ${STEP_LABEL[t]}`} variant="outline" size="sm" onPress={() => setDraft({ ...draft, steps: [...draft.steps, newStep(t)] })} />
              ))}
            </View>
            <Timeline steps={draft.steps} ctrls={ctrls} />
            {issues.length > 0 && <View style={styles.issues}>{issues.map((m, i) => <Text key={i} style={styles.issue}>• {m}</Text>)}</View>}
            <View style={styles.row2}>
              <Button title="Cancel" variant="outline" size="md" onPress={onClose} style={{ flex: 1 }} />
              <Button title="Done" size="md" onPress={save} disabled={issues.length > 0} style={{ flex: 1 }} />
            </View>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}

interface Props { bayId: string; bayType: string; ctrls: CtrlRow[]; inputs: InputRef[]; actions: Action[]; onChange: (a: Action[]) => void; catalog: Catalog; foreignUse: Record<string, number[]>; dirty: boolean; /** API prefix for test / run calls; defaults to /api/bays/:id */ apiBase?: string }

export default function AdvancedActionsEditor({ bayId, bayType, ctrls, inputs, actions, onChange, catalog, foreignUse, dirty, apiBase }: Props) {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const toast = useToast();
  const scope = useOwnerScope();
  const base = apiBase ?? `/api/bays/${bayId}`;
  const [editing, setEditing] = useState<{ index: number | null } | null>(null);
  const [confirmDel, setConfirmDel] = useState<number | null>(null);
  const [runs, setRuns] = useState<Record<string, { programId: string | null; state: string; totalMs: number; kind: 'test' | 'run' }>>({});
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const poll = (actionId: string, programId: string, tries = 0) => {
    timer.current = setTimeout(async () => {
      try {
        const r = await scope.api.get<{ runs: { id: string; state: string }[] }>(`${base}/program-runs`);
        const run = r.runs.find(x => x.id === programId);
        const st = run?.state ?? 'sent';
        setRuns(prev => ({ ...prev, [actionId]: { ...(prev[actionId] ?? { programId, totalMs: 0, kind: 'test' }), state: st } }));
        if (!['done', 'timeout', 'error', 'cancelled', 'assumed'].includes(st) && tries < 150) poll(actionId, programId, tries + 1);
      } catch { /* keep the last state */ }
    }, 1000);
  };
  const fire = async (a: Action, kind: 'test' | 'run') => {
    if (!a.id) return;
    if (dirty) { toast.show({ type: 'error', title: 'Save first', message: 'Runs use the saved configuration.' }); return; }
    try {
      const r = await scope.api.post<{ programId: string | null; mode: string; totalMs: number }>(`${base}/actions/${a.id}/${kind}`, {});
      setRuns(prev => ({ ...prev, [a.id!]: { programId: r.programId, state: r.mode === 'legacy' ? 'done' : 'sent', totalMs: r.totalMs, kind } }));
      if (r.programId) poll(a.id, r.programId);
    } catch (err: any) {
      toast.show({ type: 'error', title: kind === 'test' ? 'Test run refused' : 'Run refused', message: err.message });
    }
  };

  return (
    <View>
      <Text style={styles.help}>Each action has a trigger (when it runs) and steps (pulse, hold, wait, release, coin drop). Every time is in ms. Advanced starts empty; Simple rows are not copied in.</Text>
      {actions.length === 0 && <Text style={styles.empty}>No actions yet.</Text>}
      {actions.map((a, i) => {
        const run = a.id ? runs[a.id] : undefined;
        const trig = (a.trigger ?? (a.kind === 'tier' ? 'tier_purchased' : a.kind === 'upsell' ? 'upsell_purchased' : 'manual')) as Trigger;
        return (
          <View key={a.id ?? `new-${i}`} style={styles.actionRow}>
            <View style={{ flex: 1 }}>
              <Text style={styles.actionName}>{a.name}{a.enabled === false ? ' (disabled)' : ''}</Text>
              <Text style={styles.actionMeta}>{triggerLabel(trig, bayType)}{trig === 'schedule' && a.triggerConfig?.time ? ` ${a.triggerConfig.time} ${(a.triggerConfig.days ?? []).map(d => DAYS[d]).join(' ')}` : ''} · {a.steps.length} step{a.steps.length === 1 ? '' : 's'} · {actionTotalMs(a)} ms</Text>
              {run && <Text style={[styles.runState, run.state === 'done' && { color: colors.success }, ['timeout', 'error'].includes(run.state) && { color: colors.danger }]}>{run.kind === 'test' ? 'Test' : 'Run'}: {run.state}</Text>}
            </View>
            {trig === 'manual' && <TouchableOpacity onPress={() => fire(a, 'run')} hitSlop={6} disabled={!a.id} accessibilityLabel={`Run ${a.name}`}><Ionicons name="flash-outline" size={22} color={a.id ? colors.primary : colors.borderLight} /></TouchableOpacity>}
            <TouchableOpacity onPress={() => fire(a, 'test')} hitSlop={6} disabled={!a.id || (run && ['sent', 'started', 'queued'].includes(run.state))} accessibilityLabel={`Test ${a.name}`}><Ionicons name="play-circle-outline" size={22} color={a.id ? colors.primary : colors.borderLight} /></TouchableOpacity>
            <TouchableOpacity onPress={() => setEditing({ index: i })} hitSlop={6}><Ionicons name="create-outline" size={20} color={colors.primary} /></TouchableOpacity>
            <TouchableOpacity onPress={() => setConfirmDel(confirmDel === i ? null : i)} hitSlop={6}><Ionicons name="trash-outline" size={20} color={colors.danger} /></TouchableOpacity>
            {confirmDel === i && (
              <View style={{ width: '100%' }}>
                <InlineConfirm visible message={`Remove "${a.name}"?`} confirmLabel="Remove" onCancel={() => setConfirmDel(null)} onConfirm={() => { setConfirmDel(null); onChange(actions.filter((_, j) => j !== i)); }} />
              </View>
            )}
          </View>
        );
      })}
      <Button title="Add action" variant="outline" size="sm" onPress={() => setEditing({ index: null })} style={{ marginTop: spacing.sm, alignSelf: 'flex-start' }} />

      <Text style={styles.sub}>Relay map</Text>
      <RelayMapCard ctrls={ctrls} actions={actions} foreignUse={foreignUse} />

      <StepEditorModal
        visible={!!editing}
        action={editing && editing.index != null ? actions[editing.index] : null}
        bayType={bayType} ctrls={ctrls} inputs={inputs} catalog={catalog}
        onClose={() => setEditing(null)}
        onSave={a => {
          if (!editing) return;
          const next = editing.index == null ? [...actions, { ...a, displayOrder: actions.length }] : actions.map((x, j) => (j === editing.index ? { ...x, ...a } : x));
          onChange(next); setEditing(null);
        }}
      />
    </View>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  help: { fontSize: fontSize.xs, color: c.textSecondary, marginBottom: spacing.sm },
  empty: { fontSize: fontSize.sm, color: c.textLight, marginBottom: spacing.sm },
  sub: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.textSecondary, marginTop: spacing.md, marginBottom: spacing.xs },
  actionRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.sm, borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: c.borderLight, flexWrap: 'wrap' },
  actionName: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text },
  actionMeta: { fontSize: fontSize.xs, color: c.textSecondary },
  runState: { fontSize: fontSize.xs, color: c.textSecondary, fontWeight: fontWeight.semibold, marginTop: 2 },
  mapCard: { padding: spacing.sm, backgroundColor: c.surfaceSecondary, borderRadius: borderRadius.md, marginBottom: spacing.sm },
  mapTitle: { fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: c.text, marginBottom: spacing.xs },
  grid: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs },
  cell: { width: 84, minHeight: 48, padding: 4, borderRadius: borderRadius.sm, borderWidth: 1, borderColor: c.primary, backgroundColor: c.primary + '12' },
  cellFree: { borderColor: c.border, backgroundColor: 'transparent' },
  cellConflict: { borderColor: c.danger, backgroundColor: c.danger + '12' },
  cellForeign: { borderColor: c.border, backgroundColor: c.borderLight },
  cellLabel: { fontSize: 10, fontWeight: fontWeight.bold, color: c.textSecondary },
  cellText: { fontSize: 10, color: c.text },
  backdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.45)', justifyContent: 'flex-end' },
  sheet: { backgroundColor: c.surface, borderTopLeftRadius: borderRadius.lg, borderTopRightRadius: borderRadius.lg, padding: spacing.lg, maxHeight: '92%' },
  sheetTitle: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text, marginBottom: spacing.sm },
  row2: { flexDirection: 'row', gap: spacing.sm, marginTop: spacing.sm },
  stepCard: { padding: spacing.sm, borderWidth: 1, borderColor: c.border, borderRadius: borderRadius.md, marginBottom: spacing.sm },
  stepHead: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.xs },
  stepNum: { width: 20, fontSize: fontSize.sm, fontWeight: fontWeight.bold, color: c.textSecondary },
  boardRow: { marginBottom: spacing.xs },
  boardName: { fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: c.textSecondary },
  segment: { flexDirection: 'row', gap: spacing.xs, flexWrap: 'wrap', marginVertical: spacing.xs },
  segBtn: { paddingHorizontal: spacing.sm, paddingVertical: 4, borderRadius: borderRadius.md, borderWidth: 1, borderColor: c.border },
  segBtnActive: { borderColor: c.primary, backgroundColor: c.primary + '15' },
  segText: { fontSize: fontSize.xs, color: c.textSecondary, fontWeight: fontWeight.semibold },
  segTextActive: { color: c.primary },
  chips: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs, marginVertical: spacing.xs },
  chip: { paddingHorizontal: spacing.sm, paddingVertical: 4, borderRadius: 14, borderWidth: 1, borderColor: c.border },
  chipOn: { borderColor: c.primary, backgroundColor: c.primary },
  chipText: { fontSize: fontSize.xs, color: c.textSecondary, fontWeight: fontWeight.semibold },
  chipTextOn: { color: '#fff' },
  timeline: { marginTop: spacing.sm, padding: spacing.sm, backgroundColor: c.surfaceSecondary, borderRadius: borderRadius.md },
  tlRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginBottom: 4 },
  tlLabel: { width: 90, fontSize: 10, color: c.textSecondary },
  tlTrack: { flex: 1, height: 12, backgroundColor: c.borderLight, borderRadius: 3, overflow: 'hidden' },
  tlBar: { position: 'absolute', top: 0, bottom: 0, borderRadius: 3 },
  tlTotal: { fontSize: 10, color: c.textSecondary, textAlign: 'right', marginTop: 2 },
  issues: { marginTop: spacing.sm, padding: spacing.sm, backgroundColor: c.danger + '12', borderRadius: borderRadius.md },
  issue: { fontSize: fontSize.xs, color: c.danger },
});
